import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarHeart, Eye, FileText, Mail, Pencil, Plus, Send, ShieldCheck, Sparkles, Users, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAI_AREAS = [
  ["motoria", "Mobilità"],
  ["nutrizione", "Alimentazione"],
  ["igiene", "Autonomia"],
  ["educativa", "Socializzazione"],
  ["riposo", "Benessere"],
  ["osservazione", "Osservazione"],
];
const PERMISSIONS = [
  ["percorso:read", "Percorso PAI"], ["terapia:read", "Terapia"], ["ricette:read", "Ricette"],
  ["ricette:write", "Carica ricette"], ["documenti:read", "Documenti"], ["documenti:write", "Carica documenti"],
  ["aggiornamenti:read", "Aggiornamenti"], ["momenti:read", "Momenti"], ["amministrazione:read", "Amministrazione"],
  ["richieste:read", "Richieste"], ["richieste:write", "Apri richieste"],
];
const MOMENTO_TIPI = [
  ["attivita", "Attività"],
  ["eventi", "Eventi"],
  ["uscite", "Uscite"],
];
const INVITE_STATUS = {
  inviato: ["Invito inviato", "bg-[#EDE9F6] text-[#5a3b94]"],
  non_recapitato: ["Invito non recapitato", "bg-rose-50 text-rose-700"],
  scaduto: ["Invito scaduto", "bg-amber-50 text-amber-800"],
  accettato: ["Invito accettato", "bg-emerald-50 text-emerald-800"],
  attivo: ["Account già attivo", "bg-slate-100 text-slate-700"],
};
const DELEGA_STATUS = {
  attiva: ["Delega attiva", "bg-emerald-50 text-emerald-800"],
  sospesa: ["Sospesa", "bg-amber-50 text-amber-800"],
  revocata: ["Revocata", "bg-slate-100 text-slate-600"],
};
const EMPTY_INVITE = {
  nome: "", cognome: "", email: "", relazione: "",
  permessi: ["percorso:read", "aggiornamenti:read", "momenti:read", "documenti:read", "documenti:write", "richieste:read", "richieste:write"],
};
const EMPTY_DOC_REQ = { titolo: "", descrizione: "", scadenza: "" };
const EMPTY_MOMENTO = { titolo: "", testo: "", tipo: "attivita", data_evento: "", files: [] };
const MAX_MOMENTO_FOTO = 10;
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";
const SUBTAB_CLASS =
  "gap-2 rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm whitespace-nowrap data-[state=active]:border-[#6e48b5] data-[state=active]:text-[#5a3b94] data-[state=active]:bg-transparent data-[state=active]:shadow-none";
const FIELD_CLASS = "h-11 rounded-xl border-slate-200 bg-white";

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function toDatetimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function pickImageFiles(fileList, remaining) {
  return Array.from(fileList || []).slice(0, Math.max(0, remaining));
}

function errorDetail(error) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg || item).join(" ");
  return detail || "Non è stato possibile salvare. Riprova.";
}

function initials(nome, cognome) {
  return `${(nome || "").charAt(0)}${(cognome || "").charAt(0)}`.toUpperCase() || "F";
}

const FAMILY_DOC_STATUS = {
  caricato: "Caricato",
  da_verificare: "Da verificare",
  accettato: "Accettato",
  da_integrare: "Da integrare",
  superato: "Superato",
  archiviato: "Archiviato",
};

function familyDocStatusLabel(stato) {
  return FAMILY_DOC_STATUS[stato] || String(stato || "caricato").replaceAll("_", " ");
}

function MomentoThumb({ momento, foto, token, className = "h-24 w-32 shrink-0 rounded-xl object-cover" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!foto?.id || !token) return undefined;
    let url;
    axios.get(`${API_URL}/api/family/struttura/momenti/${momento.id}/foto/${foto.id}`, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      url = URL.createObjectURL(response.data);
      setSrc(url);
    }).catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [momento.id, foto?.id, token]);
  if (!src) return <div className={`${className} bg-[#EDE9F6]`} />;
  return <img src={src} alt="" className={className} />;
}

export default function FamilyFascicoloPanel({ fascicoloId, token }) {
  const [data, setData] = useState(null);
  const [momenti, setMomenti] = useState([]);
  const [documentiData, setDocumentiData] = useState({ richieste: [], documenti: [] });
  const [docForm, setDocForm] = useState(EMPTY_DOC_REQ);
  const [savingDoc, setSavingDoc] = useState(false);
  const [docMessage, setDocMessage] = useState("");
  const [updatingDoc, setUpdatingDoc] = useState("");
  const [openingDoc, setOpeningDoc] = useState("");
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("invito");
  const [publishing, setPublishing] = useState("");
  const [error, setError] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [momentoMessage, setMomentoMessage] = useState("");
  const [inviting, setInviting] = useState(false);
  const [savingMomento, setSavingMomento] = useState(false);
  const [resending, setResending] = useState("");
  const [form, setForm] = useState(EMPTY_INVITE);
  const [momentoForm, setMomentoForm] = useState(EMPTY_MOMENTO);
  const [momentoFileKey, setMomentoFileKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_MOMENTO);
  const [editFileKey, setEditFileKey] = useState(0);
  const [removingFoto, setRemovingFoto] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [ospite, timeline, docs] = await Promise.all([
        axios.get(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}`, { headers }),
        axios.get(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/timeline`, { headers }).catch(() => ({ data: { momenti: [] } })),
        axios.get(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/documenti`, { headers }).catch(() => ({ data: { richieste: [], documenti: [] } })),
      ]);
      setData(ospite.data);
      setMomenti(timeline.data?.momenti || []);
      setDocumentiData({ richieste: docs.data?.richieste || [], documenti: docs.data?.documenti || [] });
      if (!silent) setError("");
    } catch (e) {
      setError(errorDetail(e));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [fascicoloId, token]);

  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        URL.revokeObjectURL(viewer.url);
        setViewer(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  const saveConfig = async (next) => {
    setError("");
    try {
      await axios.put(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/config`, next, { headers });
      setData({ ...data, family_config: next });
    } catch (e) {
      setError(errorDetail(e));
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true); setError(""); setInviteMessage("");
    try {
      const response = await axios.post(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/deleghe`, form, { headers });
      if (response.data?.invito_inviato) setInviteMessage("Invito inviato. Il familiare ha 7 giorni per attivare l'account.");
      else if (["accettato", "attivo"].includes(response.data?.stato_invito)) setInviteMessage("Delega collegata a un account Family già attivo.");
      else setInviteMessage("Delega creata, ma l'email di invito non è partita. Puoi reinviare dall'elenco.");
      setForm({ ...EMPTY_INVITE, permessi: form.permessi });
      setSection("inviati");
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (delegaId) => {
    setResending(delegaId); setError(""); setInviteMessage("");
    try {
      await axios.post(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/deleghe/${delegaId}/reinvia-invito`, {}, { headers });
      setInviteMessage("Invito reinviato. Il familiare ha altri 7 giorni per attivare l'account.");
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setResending("");
    }
  };

  const togglePermission = (permission) => setForm((current) => ({
    ...current,
    permessi: current.permessi.includes(permission)
      ? current.permessi.filter((item) => item !== permission)
      : [...current.permessi, permission],
  }));

  const publishedAreas = new Set((data?.pubblicazioni || []).filter((item) => item.pubblicato).map((item) => item.area));
  const publishArea = async (area, titolo) => {
    const nextPublished = !publishedAreas.has(area);
    setPublishing(area);
    setError("");
    try {
      const response = await axios.put(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/percorso/pubblicazioni/${area}`, {
        area, pubblicato: nextPublished, titolo_famiglia: titolo, direzione: "mantenere",
      }, { headers });
      const saved = response.data?.pubblicazione || { area, pubblicato: nextPublished, titolo_famiglia: titolo };
      setData((current) => ({
        ...current,
        pubblicazioni: [...(current?.pubblicazioni || []).filter((item) => item.area !== area), saved],
      }));
    } catch (e) {
      setError(errorDetail(e));
    } finally {
      setPublishing("");
    }
  };

  const momentoPayload = (form) => ({
    titolo: form.titolo.trim(),
    testo: form.testo.trim() || null,
    tipo: form.tipo,
    data_evento: form.data_evento ? new Date(form.data_evento).toISOString() : undefined,
  });

  const uploadPhotos = async (momentoId, files) => {
    for (const file of files) {
      const body = new FormData();
      body.append("file", file);
      await axios.post(`${API_URL}/api/family/struttura/momenti/${momentoId}/foto`, body, { headers });
    }
  };

  const createMomento = async (e) => {
    e.preventDefault();
    setSavingMomento(true); setError(""); setMomentoMessage("");
    try {
      const created = await axios.post(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/momenti`, momentoPayload(momentoForm), { headers });
      const momentoId = created.data?.item?.id;
      if (momentoForm.files.length && momentoId) {
        await uploadPhotos(momentoId, momentoForm.files);
      }
      setMomentoForm(EMPTY_MOMENTO);
      setMomentoFileKey((key) => key + 1);
      setMomentoMessage("Momento pubblicato. I familiari autorizzati lo vedono in Altro → Momenti.");
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSavingMomento(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      titolo: item.titolo || "",
      testo: item.testo || "",
      tipo: item.tipo || "attivita",
      data_evento: toDatetimeLocal(item.data_evento),
      files: [],
    });
    setEditFileKey((key) => key + 1);
    setError("");
    setMomentoMessage("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_MOMENTO);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setSavingMomento(true); setError(""); setMomentoMessage("");
    try {
      await axios.patch(`${API_URL}/api/family/struttura/momenti/${editingId}`, momentoPayload(editForm), { headers });
      if (editForm.files.length) {
        await uploadPhotos(editingId, editForm.files);
      }
      setEditingId(null);
      setEditForm(EMPTY_MOMENTO);
      setMomentoMessage("Momento aggiornato. I familiari vedono titolo, descrizione e galleria aggiornati.");
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSavingMomento(false);
    }
  };

  const createDocRequest = async (e) => {
    e.preventDefault();
    setSavingDoc(true); setError(""); setDocMessage("");
    try {
      await axios.post(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/documenti/richieste`, {
        titolo: docForm.titolo.trim(),
        descrizione: docForm.descrizione.trim() || null,
        scadenza: docForm.scadenza || null,
      }, { headers });
      setDocForm(EMPTY_DOC_REQ);
      setDocMessage("Richiesta inviata. I familiari con permesso Documenti ricevono una notifica push.");
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSavingDoc(false);
    }
  };

  const setDocStatus = async (fileId, stato) => {
    setUpdatingDoc(fileId); setError("");
    try {
      await axios.patch(`${API_URL}/api/family/struttura/ospiti/${fascicoloId}/documenti/${fileId}/stato`, { stato }, { headers });
      setDocMessage(stato === "da_integrare" ? "Il familiare riceve una push per integrare il documento." : "Documento accettato.");
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setUpdatingDoc("");
    }
  };

  const openFamilyDoc = async (doc) => {
    if (!doc?.file_id) return;
    setOpeningDoc(doc.file_id);
    setError("");
    try {
      const response = await axios.get(
        `${API_URL}/api/family/struttura/ospiti/${fascicoloId}/documenti/${doc.file_id}/download`,
        { headers, responseType: "blob" },
      );
      const type = response.data?.type || doc.file_type || "application/octet-stream";
      const blob = response.data?.type ? response.data : new Blob([response.data], { type });
      const url = URL.createObjectURL(blob);
      const name = doc.file_name || "documento";
      const previewable = String(type).startsWith("image/") || type === "application/pdf" || /\.(png|jpe?g|gif|webp|pdf)$/i.test(name);
      if (previewable) {
        setViewer((current) => {
          if (current?.url) URL.revokeObjectURL(current.url);
          return { url, name, type };
        });
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      setError("Non è stato possibile aprire il documento.");
    } finally {
      setOpeningDoc("");
    }
  };

  const removeFoto = async (momentoId, fotoId) => {
    setRemovingFoto(fotoId); setError("");
    try {
      await axios.delete(`${API_URL}/api/family/struttura/momenti/${momentoId}/foto/${fotoId}`, { headers });
      await load({ silent: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setRemovingFoto("");
    }
  };

  if (loading) return <div className="py-8 text-sm text-slate-500">Caricamento Family…</div>;
  const config = data?.family_config || { abilitato: false, moduli_attivi: [], consenso_foto: false };
  const deleghe = data?.deleghe || [];
  const familyDocs = (documentiData.documenti || []).filter((doc) => doc.origine === "family");
  const pendingDocs = familyDocs.filter((doc) => doc.stato_family === "da_verificare");
  const receivedDocs = familyDocs.filter((doc) => doc.stato_family !== "da_verificare");

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-[#EDE9F6] bg-gradient-to-br from-[#F7F5FB] to-white p-5">
        <div className="flex gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#EDE9F6] text-[#6e48b5]">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Portale AFFIDYA Family</h3>
            <p className="mt-1 text-sm text-slate-600">Le informazioni restano nel fascicolo e diventano visibili solo con una delega autorizzata.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <label className="flex items-center gap-2">
            <Checkbox checked={config.abilitato} onCheckedChange={(abilitato) => saveConfig({ ...config, abilitato })} />
            Abilita per questo ospite
          </label>
          <label className="flex items-center gap-2">
            <Checkbox checked={config.consenso_foto} onCheckedChange={(consenso_foto) => saveConfig({ ...config, consenso_foto })} />
            Consenso foto verificato
          </label>
        </div>
        <div className="mt-5 border-t border-[#EDE9F6] pt-4">
          <h4 className="text-sm font-semibold text-slate-900">Obiettivi PAI pubblicabili</h4>
          <p className="mt-1 text-sm text-slate-500">Pubblica soltanto le aree adatte alla famiglia. Un secondo click nasconde l&apos;obiettivo dal portale. Il PAI professionale non cambia.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {PAI_AREAS.map(([area, titolo]) => {
              const published = publishedAreas.has(area);
              return (
                <Button
                  key={area}
                  type="button"
                  variant={published ? "default" : "outline"}
                  disabled={publishing === area}
                  onClick={() => publishArea(area, titolo)}
                  className={published
                    ? "rounded-full bg-[#6e48b5] text-white hover:bg-[#5a3b94]"
                    : "rounded-full border-[#d8ccec] text-[#5a3b94] hover:bg-[#EDE9F6]"}
                >
                  {publishing === area ? "Salvataggio…" : published ? `Pubblicato: ${titolo}` : `Pubblica ${titolo}`}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {error ? <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

      <Tabs value={section} onValueChange={setSection} className="space-y-4">
        <TabsList className="flex h-auto w-full flex-nowrap justify-start gap-0 overflow-x-auto rounded-none border-b border-slate-200 bg-transparent p-0">
          <TabsTrigger value="invito" className={SUBTAB_CLASS}>
            <Send className="h-4 w-4" />
            Invia invito
          </TabsTrigger>
          <TabsTrigger value="inviati" className={SUBTAB_CLASS}>
            <Users className="h-4 w-4" />
            Inviti inviati
            <span className="rounded-full bg-[#EDE9F6] px-1.5 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{deleghe.length}</span>
          </TabsTrigger>
          <TabsTrigger value="momenti" className={SUBTAB_CLASS}>
            <Sparkles className="h-4 w-4" />
            Momenti
            <span className="rounded-full bg-[#EDE9F6] px-1.5 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{momenti.length}</span>
          </TabsTrigger>
          <TabsTrigger value="documenti" className={SUBTAB_CLASS}>
            <FileText className="h-4 w-4" />
            Documenti
            <span className="rounded-full bg-[#EDE9F6] px-1.5 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{documentiData.richieste.length + pendingDocs.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invito" className="mt-0">
          <form onSubmit={invite} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6] text-[#6e48b5]">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-semibold text-slate-900">Invita un familiare</h3>
                <p className="mt-1 text-sm text-slate-500">Invia un account personale. Il familiare ha 7 giorni per attivarlo dal link ricevuto via email.</p>
              </div>
            </div>
            {inviteMessage && section === "invito" ? <p className="mt-4 rounded-xl bg-[#EDE9F6] px-3 py-2 text-sm text-[#5a3b94]">{inviteMessage}</p> : null}
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-slate-600">Nome</Label>
                <Input required placeholder="Nome" className={FIELD_CLASS} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-600">Cognome</Label>
                <Input required placeholder="Cognome" className={FIELD_CLASS} value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-600">Email</Label>
                <Input required type="email" placeholder="email@esempio.it" className={FIELD_CLASS} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-600">Relazione</Label>
                <Input placeholder="es. figlia" className={FIELD_CLASS} value={form.relazione} onChange={(e) => setForm({ ...form, relazione: e.target.value })} />
              </div>
            </div>
            <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50/70 p-4">
              <p className="text-sm font-semibold text-slate-800">Autorizzazioni sul portale</p>
              <p className="mt-1 text-xs text-slate-500">Seleziona solo ciò che questo familiare può vedere o fare.</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {PERMISSIONS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox checked={form.permessi.includes(key)} onCheckedChange={() => togglePermission(key)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <Button disabled={inviting} className="mt-5 h-11 rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">
              <Mail className="h-4 w-4" />
              <Plus className="h-3 w-3" />
              {inviting ? "Invio…" : "Invia invito"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="inviati" className="mt-0 space-y-3">
          {inviteMessage ? <p className="rounded-xl bg-[#EDE9F6] px-3 py-2 text-sm text-[#5a3b94]">{inviteMessage}</p> : null}
          {deleghe.length ? deleghe.map((d) => {
            const [inviteLabel, inviteStyle] = INVITE_STATUS[d.stato_invito] || INVITE_STATUS.inviato;
            const [delegaLabel, delegaStyle] = DELEGA_STATUS[d.stato] || DELEGA_STATUS.attiva;
            const fullName = [d.nome, d.cognome].filter(Boolean).join(" ") || d.relazione || "Familiare";
            return (
              <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EDE9F6] text-sm font-bold text-[#5a3b94]">
                      {initials(d.nome, d.cognome)}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{fullName}</p>
                      <p className="truncate text-sm text-slate-500">{d.email || "Email non disponibile"}{d.relazione ? ` · ${d.relazione}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${inviteStyle}`}>{inviteLabel}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${delegaStyle}`}>{delegaLabel}</span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-500">{d.permessi?.length || 0} autorizzazioni assegnate</p>
                <dl className="mt-2 grid gap-1 text-xs text-slate-500 sm:grid-cols-2">
                  {d.invito_inviato_at ? <div>Invito inviato: <span className="font-medium text-slate-700">{formatWhen(d.invito_inviato_at)}</span></div> : null}
                  {d.invito_scadenza && d.puo_reinviare ? <div>Scade: <span className="font-medium text-slate-700">{formatWhen(d.invito_scadenza)}</span></div> : null}
                  {d.attivato_at ? <div>Account attivato: <span className="font-medium text-slate-700">{formatWhen(d.attivato_at)}</span></div> : null}
                  <div>Ultimo accesso Family: <span className="font-medium text-slate-700">{d.ultimo_accesso ? formatWhen(d.ultimo_accesso) : "mai"}</span></div>
                </dl>
                {d.puo_reinviare ? (
                  <Button type="button" variant="outline" disabled={resending === d.id} onClick={() => resendInvite(d.id)} className="mt-3 h-8 rounded-lg border-[#d8ccec] px-3 text-xs text-[#5a3b94] hover:bg-[#EDE9F6]">
                    {resending === d.id ? "Invio…" : "Reinvia invito"}
                  </Button>
                ) : null}
              </div>
            );
          }) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">Nessun invito inviato</p>
              <p className="mt-1 text-sm text-slate-500">Usa la sezione Invia invito per collegare un familiare a questo ospite.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="momenti" className="mt-0 space-y-4">
          {!config.consenso_foto ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Per pubblicare Momenti registra prima il <strong>consenso foto verificato</strong> in cima a questa scheda.
            </p>
          ) : null}
          {momentoMessage ? <p className="rounded-xl bg-[#EDE9F6] px-3 py-2 text-sm text-[#5a3b94]">{momentoMessage}</p> : null}

          <form onSubmit={createMomento} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6] text-[#6e48b5]">
                <CalendarHeart className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-semibold text-slate-900">Nuovo momento</h3>
                <p className="mt-1 text-sm text-slate-500">Titolo, descrizione e galleria foto (fino a {MAX_MOMENTO_FOTO}) diventano visibili in Family, in Altro → Momenti. Non è un social: niente commenti o like.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label className="mb-1.5 block text-slate-600">Titolo</Label>
                <Input required minLength={2} placeholder="es. Laboratorio di pittura" className={FIELD_CLASS} value={momentoForm.titolo} onChange={(e) => setMomentoForm({ ...momentoForm, titolo: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-600">Categoria</Label>
                <select
                  className={`${FIELD_CLASS} w-full border px-3 text-sm`}
                  value={momentoForm.tipo}
                  onChange={(e) => setMomentoForm({ ...momentoForm, tipo: e.target.value })}
                >
                  {MOMENTO_TIPI.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-600">Data e ora</Label>
                <Input type="datetime-local" className={FIELD_CLASS} value={momentoForm.data_evento} onChange={(e) => setMomentoForm({ ...momentoForm, data_evento: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-1.5 block text-slate-600">Descrizione</Label>
                <Textarea
                  rows={3}
                  placeholder="Breve racconto da condividere con la famiglia"
                  className="min-h-[88px] rounded-xl border-slate-200 bg-white"
                  value={momentoForm.testo}
                  onChange={(e) => setMomentoForm({ ...momentoForm, testo: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-1.5 block text-slate-600">Foto (jpg, png, webp o heic, max 10 MB ciascuna, fino a {MAX_MOMENTO_FOTO})</Label>
                <Input
                  key={momentoFileKey}
                  type="file"
                  multiple
                  accept={PHOTO_ACCEPT}
                  className="h-11 rounded-xl border-slate-200 bg-white pt-2"
                  onChange={(e) => setMomentoForm({ ...momentoForm, files: pickImageFiles(e.target.files, MAX_MOMENTO_FOTO) })}
                />
                {momentoForm.files.length ? <p className="mt-1.5 text-xs text-slate-500">{momentoForm.files.length} foto selezionate: {momentoForm.files.map((file) => file.name).join(", ")}</p> : null}
              </div>
            </div>
            <Button disabled={savingMomento || !config.consenso_foto} className="mt-5 h-11 rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">
              <Sparkles className="h-4 w-4" />
              {savingMomento ? "Pubblicazione…" : "Pubblica momento"}
            </Button>
          </form>

          <div>
            <h4 className="mb-3 text-sm font-semibold text-slate-800">Momenti pubblicati</h4>
            {momenti.length ? (
              <div className="space-y-3">
                {momenti.map((item) => {
                  const tipoLabel = MOMENTO_TIPI.find(([value]) => value === item.tipo)?.[1] || item.tipo || "Momento";
                  const photos = item.foto || [];
                  const editing = editingId === item.id;
                  const remaining = MAX_MOMENTO_FOTO - photos.length;
                  if (editing) {
                    return (
                      <form key={item.id} onSubmit={saveEdit} className="rounded-2xl border border-[#d8ccec] bg-white p-4 shadow-sm">
                        <div className="mb-4 flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-[#5a3b94]">Modifica momento</p>
                          <span className="rounded-full bg-[#EDE9F6] px-2 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{tipoLabel}</span>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="md:col-span-2">
                            <Label className="mb-1.5 block text-slate-600">Titolo</Label>
                            <Input required minLength={2} className={FIELD_CLASS} value={editForm.titolo} onChange={(e) => setEditForm({ ...editForm, titolo: e.target.value })} />
                          </div>
                          <div>
                            <Label className="mb-1.5 block text-slate-600">Categoria</Label>
                            <select className={`${FIELD_CLASS} w-full border px-3 text-sm`} value={editForm.tipo} onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })}>
                              {MOMENTO_TIPI.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="mb-1.5 block text-slate-600">Data e ora</Label>
                            <Input type="datetime-local" className={FIELD_CLASS} value={editForm.data_evento} onChange={(e) => setEditForm({ ...editForm, data_evento: e.target.value })} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="mb-1.5 block text-slate-600">Descrizione</Label>
                            <Textarea
                              rows={3}
                              className="min-h-[88px] rounded-xl border-slate-200 bg-white"
                              value={editForm.testo}
                              onChange={(e) => setEditForm({ ...editForm, testo: e.target.value })}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="mb-1.5 block text-slate-600">Galleria foto ({photos.length}/{MAX_MOMENTO_FOTO})</Label>
                            {photos.length ? (
                              <div className="mb-3 flex flex-wrap gap-2">
                                {photos.map((foto) => (
                                  <div key={foto.id} className="relative">
                                    <MomentoThumb momento={item} foto={foto} token={token} className="h-20 w-20 rounded-xl object-cover" />
                                    <button
                                      type="button"
                                      disabled={removingFoto === foto.id || savingMomento}
                                      onClick={() => removeFoto(item.id, foto.id)}
                                      className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-600 shadow ring-1 ring-slate-200 hover:bg-rose-50 hover:text-rose-600"
                                      aria-label="Rimuovi foto"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="mb-3 text-xs text-slate-500">Nessuna foto. Puoi aggiungerne fino a {MAX_MOMENTO_FOTO}.</p>
                            )}
                            {remaining > 0 ? (
                              <>
                                <Input
                                  key={editFileKey}
                                  type="file"
                                  multiple
                                  accept={PHOTO_ACCEPT}
                                  className="h-11 rounded-xl border-slate-200 bg-white pt-2"
                                  onChange={(e) => setEditForm({ ...editForm, files: pickImageFiles(e.target.files, remaining) })}
                                />
                                {editForm.files.length ? <p className="mt-1.5 text-xs text-slate-500">{editForm.files.length} nuove foto: {editForm.files.map((file) => file.name).join(", ")}</p> : (
                                  <p className="mt-1.5 text-xs text-slate-500">Puoi aggiungere ancora {remaining} foto.</p>
                                )}
                              </>
                            ) : (
                              <p className="text-xs text-slate-500">Hai raggiunto il massimo di {MAX_MOMENTO_FOTO} foto. Rimuovine una per aggiungerne altre.</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button disabled={savingMomento} className="h-10 rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">
                            {savingMomento ? "Salvataggio…" : "Salva modifiche"}
                          </Button>
                          <Button type="button" variant="outline" disabled={savingMomento} onClick={cancelEdit} className="h-10 rounded-xl border-slate-200">
                            Annulla
                          </Button>
                        </div>
                      </form>
                    );
                  }
                  return (
                    <article key={item.id} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                      {photos.length ? (
                        <div className="flex shrink-0 gap-1">
                          {photos.slice(0, 3).map((foto) => (
                            <MomentoThumb key={foto.id} momento={item} foto={foto} token={token} className="h-20 w-20 rounded-xl object-cover" />
                          ))}
                        </div>
                      ) : (
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-[#F7F5FB] text-[#6e48b5]">
                          <Sparkles className="h-6 w-6" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1 py-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <p className="font-semibold text-slate-900">{item.titolo}</p>
                          <span className="rounded-full bg-[#EDE9F6] px-2 py-0.5 text-[11px] font-semibold text-[#5a3b94]">{tipoLabel}</span>
                        </div>
                        {item.testo ? <p className="mt-1 line-clamp-2 text-sm text-slate-600">{item.testo}</p> : null}
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <p className="text-xs text-slate-400">{formatWhen(item.data_evento)}</p>
                          {photos.length > 1 ? <p className="text-xs font-medium text-[#5a3b94]">{photos.length} foto</p> : null}
                        </div>
                        <Button type="button" variant="outline" onClick={() => startEdit(item)} className="mt-3 h-8 rounded-lg border-[#d8ccec] px-3 text-xs text-[#5a3b94] hover:bg-[#EDE9F6]">
                          <Pencil className="h-3.5 w-3.5" />
                          Modifica
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
                <Sparkles className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-700">Nessun momento pubblicato</p>
                <p className="mt-1 text-sm text-slate-500">Le attività e gli eventi condivisi con la famiglia compariranno qui.</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documenti" className="mt-0 space-y-4">
          <form onSubmit={createDocRequest} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6] text-[#6e48b5]">
                <FileText className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-semibold text-slate-900">Richiedi un documento</h3>
                <p className="mt-1 text-sm text-slate-500">Compare in Family in Documenti → Da inviare. I familiari con permesso Documenti ricevono una notifica push.</p>
              </div>
            </div>
            {docMessage ? <p className="mt-4 rounded-xl bg-[#EDE9F6] px-3 py-2 text-sm text-[#5a3b94]">{docMessage}</p> : null}
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label className="mb-1.5 block text-slate-600">Cosa serve</Label>
                <Input required placeholder="es. Carta d'identità, verbale 104" className={FIELD_CLASS} value={docForm.titolo} onChange={(e) => setDocForm({ ...docForm, titolo: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-1.5 block text-slate-600">Note per il familiare</Label>
                <Textarea rows={3} placeholder="Dettagli o formato richiesto" className="rounded-xl border-slate-200" value={docForm.descrizione} onChange={(e) => setDocForm({ ...docForm, descrizione: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-600">Scadenza (facoltativa)</Label>
                <Input type="date" className={FIELD_CLASS} value={docForm.scadenza} onChange={(e) => setDocForm({ ...docForm, scadenza: e.target.value })} />
              </div>
            </div>
            <Button type="submit" disabled={savingDoc} className="mt-4 rounded-xl bg-[#6e48b5] text-white hover:bg-[#5a3b94]">
              <Plus className="h-4 w-4" />
              {savingDoc ? "Invio…" : "Invia richiesta"}
            </Button>
          </form>
          {documentiData.richieste.length ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-900">Richieste aperte</h4>
              {documentiData.richieste.map((req) => (
                <article key={req.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{req.titolo}</p>
                      {req.descrizione ? <p className="mt-1 text-sm text-slate-500">{req.descrizione}</p> : null}
                    </div>
                    <span className="rounded-full bg-[#EDE9F6] px-2 py-0.5 text-[11px] font-semibold capitalize text-[#5a3b94]">{(req.stato || "richiesta").replaceAll("_", " ")}</span>
                  </div>
                  {req.scadenza ? <p className="mt-2 text-xs text-slate-400">Scadenza {req.scadenza}</p> : null}
                </article>
              ))}
            </div>
          ) : null}
          {pendingDocs.length ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-900">Da verificare</h4>
              {pendingDocs.map((doc) => (
                <article key={doc.file_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div>
                    <p className="font-semibold text-slate-900">{doc.file_name}</p>
                    <p className="text-xs text-slate-500">{doc.descrizione || "Caricato dal familiare"}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" disabled={openingDoc === doc.file_id} onClick={() => openFamilyDoc(doc)} className="h-8 rounded-lg text-xs">
                      <Eye className="h-3.5 w-3.5" />
                      {openingDoc === doc.file_id ? "Apertura…" : "Apri"}
                    </Button>
                    <Button type="button" variant="outline" disabled={updatingDoc === doc.file_id} onClick={() => setDocStatus(doc.file_id, "accettato")} className="h-8 rounded-lg text-xs">Accetta</Button>
                    <Button type="button" variant="outline" disabled={updatingDoc === doc.file_id} onClick={() => setDocStatus(doc.file_id, "da_integrare")} className="h-8 rounded-lg border-amber-200 text-xs text-amber-800">Da integrare</Button>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
          {receivedDocs.length ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-900">Inviati dalla famiglia</h4>
              {receivedDocs.map((doc) => (
                <article key={doc.file_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div>
                    <p className="font-semibold text-slate-900">{doc.file_name}</p>
                    <p className="text-xs text-slate-500">{doc.descrizione || "Caricato dal familiare"} · {familyDocStatusLabel(doc.stato_family)}</p>
                  </div>
                  <Button type="button" variant="outline" disabled={openingDoc === doc.file_id} onClick={() => openFamilyDoc(doc)} className="h-8 rounded-lg text-xs">
                    <Eye className="h-3.5 w-3.5" />
                    {openingDoc === doc.file_id ? "Apertura…" : "Apri"}
                  </Button>
                </article>
              ))}
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
      {viewer ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-6" role="dialog" aria-modal="true" onClick={() => { URL.revokeObjectURL(viewer.url); setViewer(null); }}>
          <button type="button" onClick={() => { URL.revokeObjectURL(viewer.url); setViewer(null); }} className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white" aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
          <p className="absolute bottom-5 left-1/2 max-w-[80%] -translate-x-1/2 truncate rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white">{viewer.name}</p>
          {(viewer.type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(viewer.name || "") ? (
            <img src={viewer.url} alt={viewer.name} className="max-h-[88vh] max-w-full rounded-xl object-contain" onClick={(event) => event.stopPropagation()} />
          ) : (
            <iframe title={viewer.name} src={viewer.url} className="h-[88vh] w-full max-w-5xl rounded-xl bg-white" onClick={(event) => event.stopPropagation()} />
          )}
        </div>
      ) : null}
    </div>
  );
}
